/**
 * Accept Team Invitation Page
 *
 * Allows both new users (no account) and existing users (have account)
 * to accept team invitations through a tab-based interface.
 *
 * URL format: /accept-invitation?token=xyz&email=user@example.com
 */

import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { teamService } from '../../services/team';
import { Card } from '../../components/ui/Card';
import { Alert } from '../../components/ui/Alert';
import { useAuthStore } from '../../store/authStore';

export default function AcceptInvitationPage() {
  const { t } = useTranslation('teams');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register, login, fetchUser } = useAuthStore();

  // Extract URL parameters
  const token = searchParams.get('token');
  const invitationEmail = searchParams.get('email');

  // State management
  const [activeTab, setActiveTab] = useState<'register' | 'login'>('register');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Registration form state
  const [registerData, setRegisterData] = useState({
    name: '',
    password: '',
    password_confirmation: '',
  });

  // Login form state
  const [loginPassword, setLoginPassword] = useState('');

  // Validation: token and email required
  if (!token || !invitationEmail) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg
                className="h-6 w-6 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('accept_invitation.invalid_title')}</h2>
            <p className="text-gray-600 mb-6">
              {t('accept_invitation.invalid_description')}
            </p>
            <Link to="/login" className="btn btn-primary w-full">
              {t('accept_invitation.go_to_login')}
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  // Handle registration flow
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // 1. Register user with invitation email
      await register({
        ...registerData,
        email: invitationEmail,
      });

      // 2. Auto-accept invitation (user now authenticated)
      await teamService.acceptInvitation(token);

      // 3. Refresh user data to get new account info
      await fetchUser();

      // 4. Show success and redirect
      toast.success(t('accept_invitation.register_success'));
      navigate('/dashboard');
    } catch (err: any) {
      // Handle specific errors
      const message = err?.response?.data?.message || t('accept_invitation.register_failed');
      setError(message);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle login flow
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // 1. Login user
      await login({
        email: invitationEmail,
        password: loginPassword,
      });

      // 2. Auto-accept invitation (user now authenticated)
      await teamService.acceptInvitation(token);

      // 3. Refresh user data to get new account info
      await fetchUser();

      // 4. Show success and redirect
      toast.success(t('accept_invitation.login_success'));
      navigate('/dashboard');
    } catch (err: any) {
      // Handle specific errors
      const message = err?.response?.data?.message || t('accept_invitation.login_failed');
      setError(message);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render UI with tabs
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        {/* Icon */}
        <div className="text-center mb-6">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100 mb-4">
            <svg
              className="h-6 w-6 text-indigo-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('accept_invitation.title')}</h2>
          <p className="text-gray-600">{t('accept_invitation.subtitle')}</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-200">
          <button
            onClick={() => {
              setActiveTab('register');
              setError(null);
            }}
            className={`flex-1 py-3 px-4 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'register'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('accept_invitation.tab_no_account')}
          </button>
          <button
            onClick={() => {
              setActiveTab('login');
              setError(null);
            }}
            className={`flex-1 py-3 px-4 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'login'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('accept_invitation.tab_have_account')}
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <Alert variant="error" className="mb-6">
            {error}
          </Alert>
        )}

        {/* Registration Form */}
        {activeTab === 'register' && (
          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <label className="label" htmlFor="register-email">
                {t('accept_invitation.email_label')}
              </label>
              <input
                type="email"
                id="register-email"
                value={invitationEmail}
                disabled
                className="input bg-gray-50 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('accept_invitation.email_note')}
              </p>
            </div>

            <div>
              <label className="label" htmlFor="name">
                {t('accept_invitation.name_label')}
              </label>
              <input
                type="text"
                id="name"
                value={registerData.name}
                onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                placeholder={t('accept_invitation.name_placeholder')}
                required
                className="input"
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label className="label" htmlFor="password">
                {t('accept_invitation.password_label')}
              </label>
              <input
                type="password"
                id="password"
                value={registerData.password}
                onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                placeholder={t('accept_invitation.password_placeholder')}
                required
                minLength={8}
                className="input"
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label className="label" htmlFor="password_confirmation">
                {t('accept_invitation.confirm_password_label')}
              </label>
              <input
                type="password"
                id="password_confirmation"
                value={registerData.password_confirmation}
                onChange={(e) =>
                  setRegisterData({ ...registerData, password_confirmation: e.target.value })
                }
                placeholder={t('accept_invitation.confirm_password_placeholder')}
                required
                minLength={8}
                className="input"
                disabled={isSubmitting}
              />
            </div>

            <button type="submit" disabled={isSubmitting} className="btn btn-primary w-full">
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                  {t('accept_invitation.creating_account')}
                </>
              ) : (
                t('accept_invitation.create_and_join')
              )}
            </button>
          </form>
        )}

        {/* Login Form */}
        {activeTab === 'login' && (
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="label" htmlFor="login-email">
                {t('accept_invitation.email_label')}
              </label>
              <input
                type="email"
                id="login-email"
                value={invitationEmail}
                disabled
                className="input bg-gray-50 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('accept_invitation.email_note')}
              </p>
            </div>

            <div>
              <label className="label" htmlFor="login-password">
                {t('accept_invitation.password_label')}
              </label>
              <input
                type="password"
                id="login-password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                placeholder={t('accept_invitation.login_password_placeholder')}
                required
                className="input"
                disabled={isSubmitting}
              />
            </div>

            <button type="submit" disabled={isSubmitting} className="btn btn-primary w-full">
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                  {t('accept_invitation.logging_in')}
                </>
              ) : (
                t('accept_invitation.login_and_join')
              )}
            </button>

            <div className="text-center mt-4">
              <Link to="/forgot-password" className="text-sm text-indigo-600 hover:text-indigo-500">
                {t('accept_invitation.forgot_password')}
              </Link>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
}
